import type { ApiResponse } from '@_/types';
import type { UseMutationResult } from '@tanstack/react-query';
import type { IntlShape } from 'react-intl';
import { DateTime } from 'luxon';
import { useEffect, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useCountdown } from 'usehooks-ts';
import { z } from 'zod';
import { ErrorCode } from '@_/constants';
import { createFormMutationSubmitHandler, useFormMutation } from '@_/hooks';
import { buttonCommonI18n } from '@_/i18n-common';
import { Button, ErrorMessage, Heading, Text } from '@_/ui';
import { isApiError } from '@_/utils-frontend';
import { BaseForm, FormOTPInput, FormSubmitButton } from '../../formElements/index.ts';

const createFormSchema = ({ intl }: { intl: IntlShape }) =>
  z.object({
    otp: z
      .string()
      .trim()
      .min(
        8,
        intl.formatMessage(
          {
            id: 'component:auth.error.otp-digits',
            defaultMessage: 'Please enter all {num} digits'
          },
          {
            num: 8
          }
        )
      )
  });

type FormSchema = z.input<ReturnType<typeof createFormSchema>>;

type RequestOtpMutation = UseMutationResult<
  ApiResponse<{ canResendAt: string }>,
  Error,
  { email: string }
>;

export interface OTPStepProps {
  canResendAt: DateTime | null;
  email: string;
  onNext: (data: { otp: string }) => void;
  requestOTPMutation: RequestOtpMutation;
}

export function OTPStep({ canResendAt, email, onNext, requestOTPMutation }: OTPStepProps) {
  const intl = useIntl();

  const formSchema = useMemo(() => createFormSchema({ intl }), [intl]);

  const form = BaseForm.useAppForm({
    defaultValues: {
      otp: ''
    } as FormSchema,
    onSubmit: createFormMutationSubmitHandler(),
    validators: {
      onSubmit: formSchema
    }
  });

  const { mutation, submit } = useFormMutation({
    form,
    formSchema,
    mutationOptions: {
      mutationFn: async data => {
        return onNext(data);
      },
      onError: error => {
        if (isApiError(error) && error.code === ErrorCode.InvalidOTP) {
          form.setFieldValue('otp', '');
        }
      }
    }
  });

  let errorMessage: React.ReactNode = false;
  if (requestOTPMutation.error || mutation.error) {
    errorMessage = true;

    if (isApiError(mutation.error) && mutation.error.code === ErrorCode.InvalidOTP) {
      errorMessage = (
        <FormattedMessage
          id="component:auth.error.invalid-otp"
          defaultMessage="The code you entered is incorrect"
        />
      );
    } else if (
      isApiError(requestOTPMutation.error) &&
      requestOTPMutation.error.code === ErrorCode.ResendEmailRateLimit
    ) {
      errorMessage = (
        <FormattedMessage
          id="component:auth.error.too-many-resend-requests"
          defaultMessage="Too many code requests. Please wait a few minutes and try again."
        />
      );
    }
  }

  return (
    <div>
      <Heading level={2}>
        <FormattedMessage
          id="component:auth.heading.finish-login"
          defaultMessage="Finish logging in"
        />
      </Heading>

      <form onSubmit={submit}>
        <div className="mt-6 flex flex-col gap-y-8">
          <Text>
            <FormattedMessage
              id="component:auth.prompt.finish-login"
              defaultMessage="Once you enter the code we sent to {email}, you'll be logged in."
              values={{
                email
              }}
            />
          </Text>

          {!!errorMessage && <ErrorMessage message={errorMessage} />}

          <div className="flex justify-center">
            <form.AppField
              name="otp"
              children={() => <FormOTPInput autoFocus digits={8} onComplete={() => submit()} />}
            />
          </div>

          <form.AppForm>
            <FormSubmitButton
              fillContainer
              loading={requestOTPMutation.isPending ? true : undefined}
              onClick={() => submit}
              size="lg"
              type="submit"
            >
              <FormattedMessage {...buttonCommonI18n.continue} />
            </FormSubmitButton>
          </form.AppForm>

          <Text block subdued>
            <span>
              <FormattedMessage
                id="component:auth.did-not-get-code"
                defaultMessage="Didn't get the code?"
              />{' '}
            </span>
            <span>
              <ResendCounter
                canResendAt={canResendAt}
                email={email}
                mutation={requestOTPMutation}
              />
            </span>
          </Text>
        </div>
      </form>
    </div>
  );
}

function ResendCounter({
  canResendAt,
  email,
  mutation
}: {
  canResendAt: DateTime | null;
  email: string;
  mutation: RequestOtpMutation;
}) {
  const now = DateTime.local();
  const [numRetrySeconds, { resetCountdown, startCountdown }] = useCountdown({
    countStart: canResendAt ? canResendAt.toUnixInteger() - now.toUnixInteger() : 0
  });

  useEffect(() => {
    resetCountdown();
    startCountdown();
  }, [resetCountdown, startCountdown]);

  return numRetrySeconds > 0 ? (
    <FormattedMessage
      id="component:auth.resend-in-seconds"
      defaultMessage="Resend in {num} seconds"
      values={{ num: numRetrySeconds }}
    />
  ) : (
    <Button
      color="primary"
      disabled={mutation.isPending}
      onClick={() => mutation.mutateAsync({ email })}
      variant="plain"
    >
      <span className="font-normal underline">
        <FormattedMessage id="component:auth.button.resend-code" defaultMessage="Resend code" />
      </span>
    </Button>
  );
}
